import React, { useState } from "react";
import {
  StyleSheet,
  TextInput,
  View,
  Platform,
  type TextInputProps,
  type StyleProp,
  type ViewStyle,
  type TextStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Fonts } from "@/constants/ThemeColours";
import { ThemedText } from "./ThemedText";
import { trackInputUsed } from "@/services/analytics";

export type ThemedInputProps = TextInputProps & {
  lightColor?: string;
  darkColor?: string;
  type?: "default" | "title" | "defaultSemiBold" | "subtitle" | "link";
  error?: string | null;
  isValid?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  leftIcon?: React.ReactNode | keyof typeof Ionicons.glyphMap;
};

/**
 * Standard themed input supporting read-only shading, theme colors, left icon, and inline validation icons.
 */
export function ThemedInput({
  style,
  containerStyle,
  lightColor,
  darkColor,
  type = "default",
  onFocus,
  onBlur,
  multiline,
  error,
  isValid,
  leftIcon,
  ...rest
}: ThemedInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const hasError = Boolean(error && error.trim().length > 0);

  const isReadOnly = rest.editable === false || (rest as any).readOnly === true;

  const defaultTextColor = useThemeColor({ light: lightColor, dark: darkColor }, "inputText");
  const readOnlyTextColor = useThemeColor({ light: lightColor, dark: darkColor }, "inputReadOnlyText");
  const textColor = isReadOnly ? readOnlyTextColor : defaultTextColor;

  const placeholderColor = useThemeColor({ light: lightColor, dark: darkColor }, "textMuted");

  const editableBg = useThemeColor({ light: lightColor, dark: darkColor }, "inputEditable");
  const readOnlyBg = useThemeColor({ light: lightColor, dark: darkColor }, "inputReadOnly");
  const backgroundColor = isReadOnly ? readOnlyBg : editableBg;

  const standardBorder = useThemeColor({ light: lightColor, dark: darkColor }, "inputEditableBorder");
  const readOnlyBorder = useThemeColor({ light: lightColor, dark: darkColor }, "inputReadOnlyBorder");
  const activeBorderColor = useThemeColor({ light: lightColor, dark: darkColor }, "tint");
  const hoverBorderColor = useThemeColor({}, "buttonHoverBorder");
  const errorColor = "#EF4444";

  const borderColor = isReadOnly
    ? readOnlyBorder
    : hasError
    ? errorColor
    : isFocused
    ? activeBorderColor
    : isHovered
    ? hoverBorderColor
    : standardBorder;

  const handleFocus = (e: any) => {
    setIsFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e: any) => {
    setIsFocused(false);
    const inputIdentifier = rest.placeholder || (rest as any)["aria-label"] || rest.accessibilityLabel || type || "input";
    trackInputUsed(String(inputIdentifier), undefined, { type });
    if (onBlur) onBlur(e);
  };

  const flattenedStyle = (StyleSheet.flatten(style) || {}) as TextStyle;
  const {
    margin,
    marginVertical,
    marginHorizontal,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    flex,
    width,
    minWidth,
    maxWidth,
    alignSelf,
    // Extract container-only styles so borders and background are handled on the outer container
    borderWidth: _borderWidth,
    borderColor: _borderColor,
    borderRadius: _borderRadius,
    borderStyle: _borderStyle,
    backgroundColor: _customBg,
    ...inputSpecificStyles
  } = flattenedStyle;

  const layoutStyle: ViewStyle = {
    ...(margin !== undefined ? { margin } : {}),
    ...(marginVertical !== undefined ? { marginVertical } : {}),
    ...(marginHorizontal !== undefined ? { marginHorizontal } : {}),
    ...(marginTop !== undefined ? { marginTop } : {}),
    ...(marginBottom !== undefined ? { marginBottom } : {}),
    ...(marginLeft !== undefined ? { marginLeft } : {}),
    ...(marginRight !== undefined ? { marginRight } : {}),
    ...(flex !== undefined ? { flex } : {}),
    ...(width !== undefined ? { width } : {}),
    ...(minWidth !== undefined ? { minWidth } : {}),
    ...(maxWidth !== undefined ? { maxWidth } : {}),
    ...(alignSelf !== undefined ? { alignSelf } : {}),
  };

  return (
    <View style={[styles.wrapper, layoutStyle, containerStyle]}>
      <View
        style={[
          styles.inputContainer,
          multiline && styles.multilineContainer,
          {
            backgroundColor,
            borderColor,
          },
          isReadOnly && styles.readOnlyInput,
        ]}
        {...(Platform.select({
          web: {
            onMouseEnter: () => !isReadOnly && setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
          } as any,
          default: {},
        }))}
      >
        {leftIcon ? (
          <View style={styles.leftIconWrapper}>
            {typeof leftIcon === "string" ? (
              <Ionicons name={leftIcon as any} size={16} color={placeholderColor} />
            ) : (
              leftIcon
            )}
          </View>
        ) : null}
        <TextInput
          style={[
            styles.inputBase,
            multiline && styles.multilineInput,
            leftIcon && styles.inputWithLeftIcon,
            { color: textColor },
            type === "default" ? styles.default : undefined,
            type === "title" ? styles.title : undefined,
            type === "defaultSemiBold" ? styles.defaultSemiBold : undefined,
            type === "subtitle" ? styles.subtitle : undefined,
            type === "link" ? styles.link : undefined,
            inputSpecificStyles,
          ]}
          placeholderTextColor={placeholderColor}
          onFocus={handleFocus}
          onBlur={handleBlur}
          multiline={multiline}
          {...rest}
        />
        {hasError ? (
          <View style={styles.inlineIconWrapper}>
            <Ionicons name="alert-circle" size={18} color={errorColor} />
          </View>
        ) : isValid ? (
          <View style={styles.inlineIconWrapper}>
            <Ionicons name="checkmark-circle" size={18} color={activeBorderColor} />
          </View>
        ) : null}
      </View>
      {/* Error tooltip floats absolutely — zero layout shift for siblings */}
      {hasError && error ? (
        <ThemedText style={styles.errorText}>{error}</ThemedText>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    marginVertical: 2,
    // overflow: visible so the absolutely-positioned error tooltip can escape
    overflow: "visible",
  },
  inputContainer: {
    height: 36,
    borderWidth: 1,
    borderRadius: 0,
    flexDirection: "row",
    alignItems: "center",
    overflow: "hidden",
    ...Platform.select({
      android: {
        minHeight: 40,
      },
      default: {},
    }),
    transitionProperty: "border-color",
    transitionDuration: "150ms",
  } as any,
  multilineContainer: {
    height: undefined,
    minHeight: 64,
    alignItems: "flex-start",
  },
  inputBase: {
    flex: 1,
    height: "100%",
    paddingHorizontal: 10,
    fontSize: 13,
    fontFamily: Fonts.sans,
    borderWidth: 0,
    borderRadius: 0,
    backgroundColor: "transparent",
    ...Platform.select({
      android: {
        paddingVertical: 0,
        textAlignVertical: "center",
        includeFontPadding: false,
      },
      web: {
        outlineStyle: "none",
        outlineWidth: 0,
      } as any,
      default: {},
    }),
  },
  multilineInput: {
    minHeight: 64,
    paddingVertical: 6,
    textAlignVertical: "top",
    fontFamily: Fonts.sans,
    borderWidth: 0,
    borderRadius: 0,
    ...Platform.select({
      android: {
        includeFontPadding: false,
      },
      default: {},
    }),
  },
  leftIconWrapper: {
    paddingLeft: 10,
    justifyContent: "center",
    alignItems: "center",
  },
  inputWithLeftIcon: {
    paddingLeft: 6,
  },
  inlineIconWrapper: {
    paddingRight: 10,
    justifyContent: "center",
    alignItems: "center",
  },
  errorText: {
    // Float absolutely immediately below the input — never displaces sibling elements
    position: "absolute",
    top: "100%",
    marginTop: 1,
    left: 2,
    right: 0,
    zIndex: 10,
    color: "#EF4444",
    fontSize: 10,
    lineHeight: 12,
    fontWeight: "500",
    backgroundColor: "transparent",
  },
  readOnlyInput: {
    opacity: 0.9,
  },
  default: {
    fontSize: 14,
  },
  defaultSemiBold: {
    fontSize: 14,
    fontWeight: "600",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
  },
  subtitle: {
    fontSize: 17,
    fontWeight: "bold",
  },
  link: {
    fontSize: 14,
    color: "#0a7ea4",
  },
});
